import * as ACT from './actions'
import { createReducer } from 'redux-create-reducer'
import update from 'immutability-helper'
import cloneDeep from 'lodash/cloneDeep'

const initialState = {
  name: '',
  proposals: [],
  dealCompanies: [],
  tasks: [],
  templates: [],
  selectedProposalTemplate: null,
  editedSelectedProposalTemplate: null,
  proposalsBoard: [],
  notes: [],
  status: {},
  healthError: false,
  summaryInfo: {},
  analysis: {},
  shouldUpdateSidebar: 0,
  documentsCount: 0
}

const updateProposalsList = (state, action) => {
  const { proposals } = action.payload
  let newState = cloneDeep(state)
  proposals.forEach(proposal => {
    const index = newState.proposals.findIndex(p => p.id === proposal.id)
    newState = update(newState, {
      proposals: {
        [index]: { $set: proposal }
      }
    })
  })
  return newState
}

const dealReducer = createReducer(initialState, {
  [ACT.DEAL_FETCH_SUCCESS](state, action) {
    const { deal } = action.payload
    return {
      ...state,
      ...deal
    }
  },

  [ACT.CLEAR_DEAL_FROM_STORE]() {
    return {
      ...initialState
    }
  },

  [ACT.DEAL_PROPERTY_UPDATE](state, action) {
    const { property, value } = action.payload
    return {
      ...state,
      [property]: value
    }
  },

  [ACT.PROPOSAL_CREATE_SUCCESS](state, action) {
    const { proposal, fromDashboard } = action.payload
    if (fromDashboard) {
      return state
    }
    return update(state, {
      proposals: { $push: [proposal] },
      proposalsBoard: {
        CREATION: { $push: [proposal] }
      }
    })
  },

  [ACT.PROPOSAL_FETCH_SUCCESS](state, action) {
    const { proposal } = action.payload
    const index = state.proposals.findIndex(p => p.id === proposal.id)
    return update(state, {
      proposals: {
        [index]: { $set: proposal }
      }
    })
  },

  [ACT.DEAL_COMPANIES_FETCH_SUCCESS](state, action) {
    const { dealCompanies } = action.payload
    return {
      ...state,
      dealCompanies
    }
  },

  [ACT.DEAL_TASKS_FETCH_SUCCESS](state, action) {
    const { tasks, dealName } = action.payload
    return {
      ...state,
      tasks,
      name: dealName || state.name
    }
  },

  [ACT.DEAL_TASK_MARK_COMPLETED_SUCCESS](state, action) {
    const { task } = action.payload
    const index = state.tasks.findIndex(t => t.id === task.id)
    return update(state, {
      tasks: {
        [index]: { $set: task }
      }
    })
  },

  [ACT.UPDATE_DEAL_TASK_SUCCESS](state, action) {
    const { task } = action.payload
    const index = state.tasks.findIndex(t => t.id === task.id)
    return update(state, {
      tasks: {
        [index]: { $set: task }
      }
    })
  },

  [ACT.DELETE_DEAL_TASK_SUCCESS](state, action) {
    const { task } = action.payload
    const index = state.tasks.findIndex(t => +t.id === task.id)

    return update(state, {
      tasks: {
        $splice: [[index, 1]]
      }
    })
  },

  [ACT.DEAL_PROPOSALS_TEMPLATES_FETCH_SUCCESS](state, action) {
    const { templates } = action.payload
    return {
      ...state,
      templates
    }
  },

  [ACT.PROPOSALS_DEAL_BOARD_DATA_FETCH_SUCCESS](state, action) {
    const { proposalsBoard } = action.payload
    return {
      ...state,
      proposalsBoard
    }
  },

  [ACT.DEAL_PROPOSAL_UPDATE_STATUS_SUCCESS](state, action) {
    const { id, oldStatus, status, proposals } = action.payload
    const index = state.proposalsBoard[oldStatus].findIndex(p => p.id === id)

    const newState = update(state, {
      proposalsBoard: {
        [oldStatus]: { $splice: [[index, 1]] },
        [status]: { $set: proposals[status] }
      }
    })
    return newState
  },

  [ACT.CREATE_DEAL_PROPOSAL_TASK_SUCCESS](state, action) {
    const { status, proposals } = action.payload
    return update(state, {
      proposalsBoard: {
        [status]: { $set: proposals[status] }
      }
    })
  },

  [ACT.UPDATE_DEAL_BOARD_PROPOSAL_TASK_SUCCESS](state, action) {
    const { status, proposal } = action.payload
    const index = state.proposalsBoard[status].findIndex(p => +p.id === proposal.id)

    return update(state, {
      proposalsBoard: {
        [status]: {
          [index]: { $set: proposal }
        }
      }
    })
  },

  [ACT.CREATE_DEAL_TASK_SUCCESS](state, action) {
    const { tasks } = action.payload
    return update(state, {
      tasks: { $push: tasks }
    })
  },

  [ACT.DEAL_PROPOSALS_ADD_SELECTED_TEMPLATE_TO_STORE](state, action) {
    const { selectedProposalTemplate } = action.payload
    return {
      ...state,
      selectedProposalTemplate
    }
  },

  [ACT.DEAL_PROPOSALS_ADD_EDITED_SELECTED_TEMPLATE_TO_STORE](state, action) {
    const { editedSelectedProposalTemplate } = action.payload
    return {
      ...state,
      editedSelectedProposalTemplate
    }
  },

  [ACT.DEAL_GENERATE_PROPOSALS_SUCCESS](state, action) {
    const { proposal } = action.payload
    const { proposalsList, proposalsBoard } = proposal

    return update(state, {
      proposalsBoard: { $set: proposalsBoard },
      proposals: { $set: proposalsList }
    })
  },

  [ACT.DEAL_ADD_NOTE_SUCCESS](state, action) {
    const { note } = action.payload

    return update(state, {
      notes: { $unshift: [note] }
    })
  },

  [ACT.DEAL_NOTES_FETCH_SUCCESS](state, action) {
    const { notes } = action.payload

    return update(state, {
      notes: { $set: notes }
    })
  },

  [ACT.DEAL_UPDATE_NOTE_SUCCESS](state, action) {
    const { note, noteId } = action.payload
    const index = state.notes.findIndex(note => note.id === noteId)

    return update(state, {
      notes: {
        [index]: { $set: { ...note } }
      }
    })
  },

  [ACT.PROPOSALS_SEND_FOR_REVIEW_SUCCESS](state, action) {
    return updateProposalsList(state, action)
  },

  [ACT.APPROVE_PROPOSALS_SUCCESS](state, action) {
    return updateProposalsList(state, action)
  },

  [ACT.DEAL_HEALTH_ERROR](state, action) {
    const { healthError } = action.payload
    return update(state, {
      healthError: { $set: healthError }
    })
  },

  [ACT.DEAL_SUMMARY_INFO_FETCH_SUCCESS](state, action) {
    const { summaryInfo } = action.payload
    return {
      ...state,
      summaryInfo
    }
  },

  [ACT.FETCH_DEAL_ANALYSIS_SUCCESS](state, action) {
    const { analysis } = action.payload
    return {
      ...state,
      analysis
    }
  },
  [ACT.DEAL_DELETE_NOTE_SUCCESS](state, action) {
    const { noteId } = action.payload

    return update(state, {
      notes: { $set: state.notes.filter(n => n.id !== noteId) }
    })
  },
  [ACT.DEAL_UPDATE_SIDEBAR](state) {
    return update(state, {
      shouldUpdateSidebar: { $set: state.shouldUpdateSidebar + 1 }
    })
  },
  [ACT.FETCH_DEAL_DOCUMENTS_COUNT_SUCCESS](state, action) {
    const { count } = action.payload

    return update(state, {
      documentsCount: { $set: count }
    })
  }
})

export default dealReducer
